import { connect } from "react-redux";

import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { withFocusOnNextValueEditor } from "../../../Helpers/JumpByEnterOnNextControl/WithFocusOnNextValueEditor";
import { checkIsSuitableToClick, externalOnClickHandler } from "../../../Engine/ActionCreators/ExternalOnClickAction";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { WithHighlightedInjectableProps, withHighlighting } from "../../withHighlighting";

import { dateConverter, dateConverterFss } from "./DateConverter";
import { DatePicker } from "./DatePicker";

interface DataPickerContainerProps extends FormActionHandlerExecutionInjectableProps, WithHighlightedInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    dateFormat?: "fss";
    viewModelSelect: ViewModelSelect;
    callHelperOnChange?: FormActionHandler;
    callHelperOnBlur?: FormActionHandler;
    disabled2?: EvaluableProp<boolean>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    isReadOnly?: boolean;
}

const mapStateToProps = (state: FullFormState, ownProps: DataPickerContainerProps) => {
    const { bindingPath, dateFormat, viewModelSelect, disabled2, processEvaluableProp } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);
    const converter = dateFormat === "fss" ? dateConverterFss : dateConverter;

    const value = converter.toControl(controlModel.value);

    return {
        value: value,
        disabled: processEvaluableProp(state, disabled2) || controlModel.disabled,
        isTooltipEnabled: controlModel.isTooltipEnabled,
        error: controlModel.error,
        warning: controlModel.warning,
        highlighted: ownProps.highlighted,
        isReadOnly: state.settings.isReadOnly,
        isLink: checkIsSuitableToClick(state.settings, bindingPath),
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: DataPickerContainerProps) => {
    const { bindingPath, dateFormat } = ownProps;
    const converter = dateFormat === "fss" ? dateConverterFss : dateConverter;
    const convertValue = (value: string | undefined) => converter.toModel(value);

    return {
        onChange: async (value: string | undefined) => {
            dispatch(setToModel(bindingPath, "value", convertValue(value)));
            if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnChange);
            }
        },
        onBlur: async () => {
            if (isNotNullOrUndefined(ownProps.callHelperOnBlur)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnBlur);
            }
        },
        onClick: () => externalOnClickHandler(dispatch, bindingPath),
    };
};

export const DatePickerContainer = withFocusOnNextValueEditor(
    withResolvePaths(
        withFormActionHandlerExecution(
            withViewModelSelect(
                withEvaluators(
                    withHighlighting(
                        connect(
                            mapStateToProps,
                            mapDispatchToProps
                        )(
                            withFocusManagement(
                                withExternalMountingEventReporting(
                                    withTestPageObjectRegistration(withControlDebugInfo(DatePicker))
                                )
                            )
                        )
                    )
                )
            )
        )
    )
);
